import * as React from 'react';

import type {GetInvoicePreview_invoicePreview as InvoicePreview} from 'nutshell-graphql-types';

import type {AddOns, SelectedAddOn, Product, BillingRoute} from '../types';

import {AddOnPickerCard} from './add-on-picker-card';
import {getAddOnInfo, getAddOnKey, getAddOnsForCurrentView} from './helpers';

type Props = {
    addOns: AddOns;
    selectedAddOns: SelectedAddOn[];
    setSelectedAddOns: (arg1: (prevSelectedAddOns: SelectedAddOn[]) => SelectedAddOn[]) => void;
    onSelectedPlanChange: (
        product: Product,
        planCode: string,
        setSelectedAddOns: (arg1: (prevSelectedAddOns: SelectedAddOn[]) => SelectedAddOn[]) => void,
        quantity?: number
    ) => void;
    onPlanIsEnabledChange: (
        product: Product,
        planCode: string,
        setSelectedAddOns: (arg1: (prevSelectedAddOns: SelectedAddOn[]) => SelectedAddOn[]) => void,
        quantity?: number | null | undefined,
        keepEnabled?: boolean
    ) => void;
    onOpenModal: () => void;
    cardWidth?: string;
    route: BillingRoute;
    invoicePreview?: InvoicePreview | null | undefined;
};

export function AddOnPicker(props: Props) {
    const {addOns} = props;

    const filteredAddOns = getAddOnsForCurrentView(addOns, props.route);

    const [addOnKeyToSortBy, setAddOnKeyToSortBy] = React.useState<any>(null);

    const addOnKeys = Object.keys(filteredAddOns);
    addOnKeys.sort((addOnKey1, addOnKey2) => {
        // We want AddOnKeyToSortBy to be the first element in the sorted array
        if (addOnKey1 === addOnKeyToSortBy) return -1;
        if (addOnKey2 === addOnKeyToSortBy) return 1;

        return 0;
    });

    React.useEffect(() => {
        if (!addOnKeyToSortBy) {
            // Get the add-on key from the URL hash to flag it as the first
            // add-on we want to show on the page
            const hash = window.location.hash;

            const addOnKey = getAddOnKey(hash);
            if (addOnKey) {
                setAddOnKeyToSortBy(addOnKey);
            }
        }
    }, [addOnKeyToSortBy]);

    return (
        <div className='flex flex-dir-col gap-32 mt-16 width-fit-content'>
            {addOnKeys.map((addOnKey) => {
                // @ts-expect-error
                if (!addOns || addOns[addOnKey].plans.length === 0) {
                    return undefined;
                }

                const addOnInfo = getAddOnInfo(
                    // @ts-expect-error
                    addOns[addOnKey],
                    props.selectedAddOns,
                    props.invoicePreview
                );

                return (
                    <AddOnPickerCard
                        key={addOnKey}
                        plans={addOnInfo.plans}
                        userQuantityTerm={addOnInfo.userQuantityTerm}
                        selectedQuantity={addOnInfo.selectedQuantity}
                        isUserQuantityBased={addOnInfo.isUserQuantityBased}
                        isCycleBased={addOnInfo.isCycleBased}
                        isEnabled={addOnInfo.isEnabled}
                        isIncluded={addOnInfo.isIncluded}
                        defaultPlanCode={addOnInfo.defaultCode}
                        selectedPlanCode={addOnInfo.selectedPlanCode}
                        infoMessage={addOnInfo.infoMessage}
                        errorMessage={addOnInfo.errorMessage}
                        onSelectedPlanChange={props.onSelectedPlanChange}
                        onIsEnabledChange={() =>
                            props.onPlanIsEnabledChange(
                                addOnInfo.product,
                                addOnInfo.selectedPlanCode || addOnInfo.defaultCode,
                                props.setSelectedAddOns,
                                addOnInfo.selectedQuantity || addOnInfo.defaultQuantity
                            )
                        }
                        setSelectedAddOns={props.setSelectedAddOns}
                        onOpenModal={props.onOpenModal}
                        cardWidth={props.cardWidth}
                    />
                );
            })}
        </div>
    );
}
